import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency, formatDateBR } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Car, Route, CalendarClock, TrendingUp, Wallet, Truck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/contexts/AuthContext";

const NITA_EMAIL = "nyttapatrocinio06@gmail.com";
const NITA_IMG =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/wa66us1e_NITA.png";

const TOOLTIP_STYLE = { background: "white", border: "1px solid hsl(var(--border))", borderRadius: 8 };
const BAR_RADIUS = [6, 6, 0, 0];

const Stat = ({ icon: Icon, label, value, sub, accent = "primary", testid }) => (
    <Card className="border-border shadow-sm hover:shadow-md transition-shadow animate-fade-in-up" data-testid={testid}>
        <CardContent className="p-6">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="mt-2 text-3xl font-extrabold text-foreground">{value}</div>
                    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
                </div>
                <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        accent === "primary" ? "bg-primary/10 text-primary" : "bg-accent/20 text-accent-foreground"
                    }`}
                >
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </CardContent>
    </Card>
);

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/dashboard");
            setData(res.data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return <div className="text-muted-foreground" data-testid="dashboard-loading">Carregando...</div>;
    }
    if (!data) {
        return <div className="text-muted-foreground">Sem dados.</div>;
    }

    const chartData = (data.monthly_chart || []).map((m) => ({
        mes: m.mes.slice(5) + "/" + m.mes.slice(2, 4),
        Fixo: m.fixo,
        Diária: m.diaria,
    }));

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            {user?.email === NITA_EMAIL && (
                <div
                    className="relative overflow-hidden rounded-2xl border-2 border-emerald-800 shadow-xl"
                    data-testid="nita-welcome-banner"
                    style={{
                        background:
                            "linear-gradient(120deg, #064e3b 0%, #065f46 45%, #047857 100%)",
                    }}
                >
                    {/* Decorative leafy overlay */}
                    <div
                        className="absolute inset-0 opacity-20"
                        style={{
                            backgroundImage:
                                "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.25) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.15) 0, transparent 45%)",
                        }}
                    />
                    <div className="relative flex items-center gap-6 p-6 md:p-8">
                        <img
                            src={NITA_IMG}
                            alt="Nita Patrocínio"
                            className="w-32 md:w-40 h-auto object-contain drop-shadow-2xl shrink-0"
                            data-testid="nita-avatar"
                        />
                        <div className="flex-1 text-white">
                            <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-200 font-bold">
                                Bem-vinda de volta
                            </div>
                            <h3 className="text-3xl md:text-4xl font-extrabold mt-1 leading-tight">
                                Olá, {user?.name?.split(" ")[0] || "Nita"} 👋
                            </h3>
                            <p className="text-sm md:text-base text-emerald-50/95 mt-2 max-w-xl leading-relaxed">
                                Painel do Setor de Transporte da <strong>COOMFLONA</strong>.
                                Acesso especial às <strong>Ordens de Serviço</strong> e <strong>Notas de Combustível</strong> disponíveis no menu ao lado.
                            </p>
                            <div className="mt-3 inline-flex items-center gap-2 bg-white/15 backdrop-blur rounded-full px-3 py-1 border border-white/25">
                                <div className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                                <span className="text-xs font-semibold text-emerald-50">Cooperada — COOMFLONA</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div>
                <h2 className="text-3xl font-extrabold text-foreground">Visão Geral</h2>
                <p className="text-sm text-muted-foreground mt-1">Resumo das viagens do setor de transporte</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Users} label="Motoristas" value={data.total_drivers} testid="stat-drivers" />
                <Stat icon={Car} label="Veículos Contratados" value={data.total_vehicles_fixo} testid="stat-vehicles-fixo" />
                <Stat icon={Car} label="Veículos Diária" value={data.total_vehicles_diaria} testid="stat-vehicles-diaria" />
                <Stat icon={Route} label="Total de Viagens" value={data.total_trips} testid="stat-trips" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-border shadow-sm" data-testid="stat-valor-fixo">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Valor Mês — Contratos</div>
                                <div className="mt-2 text-3xl font-extrabold text-primary">{formatCurrency(data.valor_mes_fixo)}</div>
                                <div className="mt-1 text-xs text-muted-foreground">{data.count_mes_fixo} viagens em {data.current_month}</div>
                            </div>
                            <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                                <Wallet className="w-5 h-5" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border shadow-sm" data-testid="stat-valor-diaria">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Valor Mês — Diárias</div>
                                <div className="mt-2 text-3xl font-extrabold text-accent-foreground">{formatCurrency(data.valor_mes_diaria)}</div>
                                <div className="mt-1 text-xs text-muted-foreground">{data.count_mes_diaria} viagens em {data.current_month}</div>
                            </div>
                            <div className="w-12 h-12 bg-accent/20 text-accent-foreground rounded-xl flex items-center justify-center">
                                <TrendingUp className="w-5 h-5" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border shadow-sm" data-testid="stat-valor-frete">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Valor Mês — Fretes</div>
                                <div className="mt-2 text-3xl font-extrabold text-primary">{formatCurrency(data.valor_mes_frete || 0)}</div>
                                <div className="mt-1 text-xs text-muted-foreground">{data.count_mes_frete || 0} fretes em {data.current_month}</div>
                            </div>
                            <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                                <Truck className="w-5 h-5" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className="border-border shadow-sm" data-testid="chart-monthly">
                <CardHeader>
                    <CardTitle className="text-lg">Últimos meses — Valores por tipo</CardTitle>
                </CardHeader>
                <CardContent>
                    {chartData.length === 0 ? (
                        <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                            Sem viagens cadastradas ainda.
                        </div>
                    ) : (
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis dataKey="mes" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                                    <Tooltip
                                        formatter={(v) => formatCurrency(v)}
                                        contentStyle={TOOLTIP_STYLE}
                                    />
                                    <Legend />
                                    <Bar dataKey="Fixo" fill="hsl(var(--primary))" radius={BAR_RADIUS} />
                                    <Bar dataKey="Diária" fill="hsl(var(--accent))" radius={BAR_RADIUS} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border-border shadow-sm" data-testid="recent-trips">
                <CardHeader>
                    <CardTitle className="text-lg">Últimas viagens cadastradas</CardTitle>
                </CardHeader>
                <CardContent>
                    {(!data.recent_trips || data.recent_trips.length === 0) ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">
                            Nenhuma viagem cadastrada ainda. Vá em <strong>Contratos de Veículos</strong> ou <strong>Viagens Diárias</strong> para começar.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {data.recent_trips.map((t) => (
                                <div key={t.id} className="flex items-center justify-between py-3 px-4 rounded-lg border border-border bg-secondary/30">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.tipo === "fixo" ? "bg-primary/15 text-primary" : "bg-accent/30 text-accent-foreground"}`}>
                                            {t.tipo === "fixo" ? <Route className="w-4 h-4" /> : <CalendarClock className="w-4 h-4" />}
                                        </div>
                                        <div>
                                            <div className="font-semibold text-foreground text-sm">{t.destino}</div>
                                            <div className="text-xs text-muted-foreground">{t.responsavel_nome} · {t.veiculo_placa} · {formatDateBR(t.data)}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Badge variant={t.tipo === "fixo" ? "default" : "secondary"} className="uppercase text-[10px]">{t.tipo}</Badge>
                                        <div className="font-bold text-foreground">{formatCurrency(t.valor)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
